import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startLogin, verifyLogin } from '../lib/auth';
import NotificationModal from '../components/ui/notification-modal';
import memoarLogo from '../assets/memoar-logo.png';
import { SupportEmail } from '../components/legal/legal-page';

/**
 * Post-checkout landing. The Stripe webhook provisions the account and emails a
 * six-digit code the moment payment completes, so the code field lives right
 * here — the customer enters the code they already received and is in. No trip
 * to /login, which would fire a SECOND code. The email is asked for (not known
 * client-side: Stripe's checkout collects it, not our form) and both fields
 * autofill. "Resend" is the only place a fresh code is sent, and only on demand.
 */
const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyLogin(email, code);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!email) {
      setError('Enter the email you paid with first, then resend.');
      return;
    }
    setBusy(true);
    try {
      await startLogin(email);
      setResent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="payment-result">
      <header className="payment-result__header">
        <div className="payment-result__logo">
          <img src={memoarLogo} alt="Memoar" className="payment-result__logo-img" />
          <span className="payment-result__logo-text">memoar</span>
        </div>
      </header>

      <div className="payment-result__container">
        <div className="payment-result__icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h1 className="payment-result__title">Payment received — welcome to Memoar</h1>
        <p className="payment-result__subtitle">
          We've emailed a six-digit sign-in code to the address you paid with.
          Enter it below to open your first Memoir.
        </p>

        <div className="payment-result__card">
          <form onSubmit={submit}>
            <div className="form-group">
              <input
                type="email" required autoFocus autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="The email you paid with"
              />
            </div>
            <div className="form-group">
              <input
                type="text" required
                className="otp-input"
                inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
              />
            </div>
            <button type="submit" className="primary-button" disabled={busy || !email || code.length !== 6}>
              {busy ? 'Opening…' : 'Open my archive'}
            </button>
          </form>

          <button type="button" className="link-button" onClick={resend} disabled={busy}>
            {resent ? 'Code sent — check your inbox' : "Didn't get it? Resend code"}
          </button>
        </div>

        <div className="payment-result__note">
          <p>
            The code can take a few seconds to arrive and expires ten minutes
            after it does. Please check your spam folder if you don't see it.
          </p>
          <p>Still nothing, or a problem with your payment? Write to <SupportEmail />.</p>
        </div>
      </div>

      <footer className="payment-result__footer">
        <p>Memories preserved with care</p>
      </footer>

      <NotificationModal
        isOpen={error !== null}
        title="Sign-in failed"
        message={error ?? ''}
        variant="error"
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default PaymentSuccess;
